import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaBaby, FaChild, FaChair } from "react-icons/fa";
import { formatCurrency } from "../utils/currencyFormatter";

const AddonsSelection = ({
  addons,
  totalPrice,
  onAddonChange,
  onReturnBooking,
  isReturnBooking,
  loading,
}) => {
  // Setup initial seat prices if API not available yet
  const [babySeatOptions, setBabySeatOptions] = useState({
    infant: { selected: false, quantity: 0, price: 10 },
    child: { selected: false, quantity: 0, price: 15 },
    booster: { selected: false, quantity: 0, price: 20 },
  });

  useEffect(() => {
    const fetchAddons = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/get-addons`);
        const addonsData = response.data || [];
        setBabySeatOptions(prev => ({
          infant: {
            ...prev.infant,
            price: addonsData.find(a => a.name === "Infant Seat")?.price || prev.infant.price,
          },
          child: {
            ...prev.child,
            price: addonsData.find(a => a.name === "Child Seat")?.price || prev.child.price,
          },
          booster: {
            ...prev.booster,
            price: addonsData.find(a => a.name === "Booster Seat")?.price || prev.booster.price,
          },
        }));
      } catch (err) {
        // fallback to default values if error
      }
    };
    fetchAddons();
  }, []);

  // Utility for plus/minus actions
  const updateSeat = (type, delta) => {
    setBabySeatOptions(prev => {
      const qty = Math.max(0, (prev[type].quantity || 0) + delta);
      const selected = qty > 0;
      // Update main add-ons state
      const updated = {
        ...prev,
        [type]: { ...prev[type], selected, quantity: qty },
      };
      // Compose new baby seat details for parent
      const babySeatDetails = {
        infant: updated.infant,
        child: updated.child,
        booster: updated.booster,
        totalBabySeatPrice:
          (updated.infant.selected ? updated.infant.price * updated.infant.quantity : 0) +
          (updated.child.selected ? updated.child.price * updated.child.quantity : 0) +
          (updated.booster.selected ? updated.booster.price * updated.booster.quantity : 0),
      };
      onAddonChange({
        ...addons,
        babySeat:
          updated.infant.selected ||
          updated.child.selected ||
          updated.booster.selected,
        babySeatDetails,
      });
      return updated;
    });
  };

  // Baby Seat Toggle Handler
  const handleBabySeatMainToggle = (checked) => {
    // If unchecking, zero out all sub-seats
    if (!checked) {
      setBabySeatOptions({
        infant: { ...babySeatOptions.infant, selected: false, quantity: 0 },
        child: { ...babySeatOptions.child, selected: false, quantity: 0 },
        booster: { ...babySeatOptions.booster, selected: false, quantity: 0 },
      });
      onAddonChange({
        ...addons,
        babySeat: false,
        babySeatDetails: {
          infant: { ...babySeatOptions.infant, selected: false, quantity: 0 },
          child: { ...babySeatOptions.child, selected: false, quantity: 0 },
          booster: { ...babySeatOptions.booster, selected: false, quantity: 0 },
          totalBabySeatPrice: 0,
        },
      });
    } else {
      onAddonChange({
        ...addons,
        babySeat: true,
        babySeatDetails: {
          ...babySeatOptions,
          totalBabySeatPrice: 0,
        },
      });
    }
  };

  // Return Trip Checkbox handler (calls parent callback)
  const handleReturnTripChange = (e) => {
    if (!isReturnBooking) onReturnBooking();
  };

  return (
    <div className="addon-sheet-main">
      <strong className="mb-2 d-block">Add-ons</strong>

      {/* Baby Seat Option */}
      <div className="addon-option">
        <input
          type="checkbox"
          id="childSeatsToggle"
          className="addon-toggle"
          checked={addons.babySeat}
          onChange={e => handleBabySeatMainToggle(e.target.checked)}
        />
        <label htmlFor="childSeatsToggle" className="addon-toggle-label">
          <span className="custom-checkbox"></span>
          <span className="addon-title">Child Seats</span>
        </label>

        {addons.babySeat && (
          <div className="child-seats-options">
            {/* Infant Seat */}
            <div className="seat-option">
              <div className="seat-info">
                <div className="seat-icon"><FaBaby /></div>
                <div className="seat-details">
                  <div className="seat-title-row">
                    <strong>Infant Seat</strong>
                    <span className="seat-age">0-6 months</span>
                  </div>
                  <span className="seat-price">Each ${babySeatOptions.infant.price}</span>
                </div>
              </div>
              <div className="seat-quantity">
                <button
                  className="quantity-btn minus"
                  onClick={() => updateSeat("infant", -1)}
                  disabled={babySeatOptions.infant.quantity === 0}
                  type="button"
                >-</button>
                <span className="quantity">{babySeatOptions.infant.quantity}</span>
                <button
                  className="quantity-btn plus"
                  onClick={() => updateSeat("infant", 1)}
                  type="button"
                >+</button>
              </div>
            </div>
            {/* Child Seat */}
            <div className="seat-option">
              <div className="seat-info">
                <div className="seat-icon"><FaChild /></div>
                <div className="seat-details">
                  <div className="seat-title-row">
                    <strong>Child Seat</strong>
                    <span className="seat-age">6 months - 3 years</span>
                  </div>
                  <span className="seat-price">Each ${babySeatOptions.child.price}</span>
                </div>
              </div>
              <div className="seat-quantity">
                <button
                  className="quantity-btn minus"
                  onClick={() => updateSeat("child", -1)}
                  disabled={babySeatOptions.child.quantity === 0}
                  type="button"
                >-</button>
                <span className="quantity">{babySeatOptions.child.quantity}</span>
                <button
                  className="quantity-btn plus"
                  onClick={() => updateSeat("child", 1)}
                  type="button"
                >+</button>
              </div>
            </div>
            {/* Booster Seat */}
            <div className="seat-option">
              <div className="seat-info">
                <div className="seat-icon"><FaChair /></div>
                <div className="seat-details">
                  <div className="seat-title-row">
                    <strong>Booster Seat</strong>
                    <span className="seat-age">3-12 years</span>
                  </div>
                  <span className="seat-price">Each ${babySeatOptions.booster.price}</span>
                </div>
              </div>
              <div className="seat-quantity">
                <button
                  className="quantity-btn minus"
                  onClick={() => updateSeat("booster", -1)}
                  disabled={babySeatOptions.booster.quantity === 0}
                  type="button"
                >-</button>
                <span className="quantity">{babySeatOptions.booster.quantity}</span>
                <button
                  className="quantity-btn plus"
                  onClick={() => updateSeat("booster", 1)}
                  type="button"
                >+</button>
              </div>
            </div>
          </div>
        )}
      </div>
	  
	  
	 

{/* Return Trip Option */}
{!isReturnBooking && (
        <div className="return-trip-container mt-3">
          <input
            type="checkbox"
            id="return-trip-checkbox"
            className="return-trip-checkbox"
            checked={isReturnBooking}
            onChange={handleReturnTripChange}
          />
          <label htmlFor="return-trip-checkbox" className="return-trip-label d-flex align-items-center">
            <span className="return-trip-text">
              <span className="return-trip-title">Add Return Trip</span>
              <span className="return-trip-sub">Book a round trip!</span>
            </span>
          </label>
        </div>
      )}
	  
	  

      {/* Total Price */}
      
    </div>
  );
};

export default AddonsSelection;
